import React, { useEffect, useState } from 'react';
import {
  Button, Icon, Input, Row, Select, Typography, Radio, Empty, Spin, Popover, Form, Tag, Col, Result,
} from 'antd';
import { withRouter } from 'react-router-dom';
import Sortable from 'react-sortablejs';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ConfigBackHeading from '../components/ConfigBackHeading';
import { ViaChecklistTags } from './Checklists';
import ConfirmButton from '../../../../components/buttons/ConfirmButton';
import {
  selectAddCategoryChecklistRequest, selectAddItemChecklistRequest,
  selectAllChecklistCategories, selectAllChecklistItems,
  selectAllChecklists, selectChecklistPageDataRequest, selectEditState, selectReorderChecklistRequest, selectUpdateChecklistTitleRequest,
} from '../../../../state/ui/configuration/inspection/checklistConfigUiReducer';
import {
  cancelChecklistAddCategoryRequest, cancelChecklistAddQuestionRequest,
  cancelUpdateChecklistTitleRequest, requestAddChecklistCategory, requestAddChecklistQuestion, requestChecklistCategoryRemoval,
  requestChecklistPageData, requestChecklistQuestionRemoval, requestChecklistReorder, requestUpdateChecklistTitle,
} from '../../../../state/ui/configuration/inspection/checklistConfigUiActions';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { userIsAdmin } from '../company/user-util';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import { sortAlphabeticallyByAttribute } from './config-util';
import routeLocations from '../../../../utility/route-locations';

const { Text } = Typography;
const { Option } = Select;

/**
 * Add Category (existing)
 */
const AddCategoryPopExisting = injectIntl((Form.create({ name: 'add-category' })(({
  form, intl, ddCategories, validationBox, saveFunction, cancelFunction, popOverChangeFunction, isLoading,
}) => (
  <Row>
    <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); saveFunction(form); }}>
      <Row type="flex" align="middle" justify="end" style={{ marginBottom: '0.5em' }}>
        <Button loading={isLoading} size="small" type="primary" icon="plus" onClick={() => popOverChangeFunction()}>
          <FormattedMessage id="new" />
        </Button>
      </Row>
      <ViaValidationAlert validationError={validationBox} />
      <Form.Item required label={<FormattedMessage id="category" />}>
        { form.getFieldDecorator('categoryCode', {
          rules: [
            { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
          ],
          initialValue: undefined,
        })(
          <Row type="flex" align="middle">
            <Select
              autoFocus
              loading={isLoading}
              placeholder={intl.formatMessage({ id: 'selectOne' })}
              showSearch
              onChange={(value) => {
                form.setFieldsValue({ categoryCode: value });
              }}
              onSearch={() => {
                form.setFieldsValue({ categoryCode: undefined });
              }}
              value={form.getFieldValue('categoryCode')}
              optionLabelProp="label"
              optionFilterProp="label"
              filterOption={
                  (input, option) => (
                    option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  )}
              notFoundContent={(
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<FormattedMessage id="noUnusedCategories" />}
                />
                )}
            >
              {sortAlphabeticallyByAttribute(ddCategories, 'categoryDescription', false).map(cat => (
                <Option key={cat.categoryCode} value={cat.categoryCode} label={cat.categoryDescription}>
                  <Row type="flex" justify="space-between">
                    <Text style={{ width: '80%', whiteSpace: 'pre-wrap' }}>{cat.categoryDescription}</Text>
                    <ViaChecklistTags show={false} active={cat.active} />
                  </Row>
                </Option>
              ))}
            </Select>
          </Row>,
        )}
      </Form.Item>
      <Row type="flex" justify="end" style={{ marginTop: '-0.5em' }}>
        <Button loading={isLoading} size="small" style={{ marginRight: '0.5em' }} onClick={() => cancelFunction()}><FormattedMessage id="cancel" /></Button>
        <Button loading={isLoading} size="small" type="primary" htmlType="submit"><FormattedMessage id="save" /></Button>
      </Row>
    </Form>
  </Row>
))));
AddCategoryPopExisting.propTypes = {
  ddCategories: PropTypes.arrayOf(PropTypes.object),
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  saveFunction: PropTypes.func,
  cancelFunction: PropTypes.func,
  popOverChangeFunction: PropTypes.func,
  isLoading: PropTypes.bool,
};
AddCategoryPopExisting.defaultProps = {
  ddCategories: [],
  validationBox: null,
  saveFunction: () => {},
  cancelFunction: () => {},
  popOverChangeFunction: () => {},
  isLoading: false,
};

/**
 * Add Category (new)
 */
const AddCategoryPopNew = injectIntl((Form.create({ name: 'new-category' })(({
  form, intl, validationBox, saveFunction, cancelFunction, isLoading, duplicateCheck,
}) => (
  <Row>
    <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); saveFunction(form); }}>
      <ViaValidationAlert validationError={validationBox} />
      <Form.Item required label={<FormattedMessage id="category" />}>
        {form.getFieldDecorator('description', {
          rules: [
            {
              required: true,
              message: intl.formatMessage({ id: 'validationEmpty' }),
            },
            {
              validator(rule, value, callback) {
                if (duplicateCheck(value)) {
                  callback(intl.formatMessage({ id: 'Duplicate' }));
                }
                callback();
              },
            },
          ],
          initialValue: '',
        })(
          <Row type="flex" align="middle">
            <Input
              placeholder={intl.formatMessage({ id: 'enter' })}
              maxLength={250}
            />
          </Row>,
        )}
      </Form.Item>
      <Row type="flex" justify="end" style={{ marginTop: '-0.5em' }}>
        <Button loading={isLoading} size="small" style={{ marginRight: '0.5em' }} onClick={() => cancelFunction()}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button loading={isLoading} size="small" type="primary" htmlType="submit"><FormattedMessage id="save" /></Button>
      </Row>
    </Form>
  </Row>
))));
AddCategoryPopNew.propTypes = {
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  saveFunction: PropTypes.func,
  cancelFunction: PropTypes.func,
  isLoading: PropTypes.bool,
  duplicateCheck: PropTypes.func,
};
AddCategoryPopNew.defaultProps = {
  validationBox: null,
  saveFunction: () => {},
  cancelFunction: () => {},
  isLoading: false,
  duplicateCheck: () => {},
};

/**
 * Add Question (existing)
 */
const AddQuestionPopExisting = injectIntl((Form.create({ name: 'add-question' })(({
  form, intl, ddQuestions, validationBox, saveFunction, cancelFunction, popOverChangeFunction, isLoading,
}) => (
  <Row>
    <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); saveFunction(form); }}>
      <Row type="flex" align="middle" justify="end" style={{ marginBottom: '0.5em' }}>
        <Button loading={isLoading} size="small" type="primary" icon="plus" onClick={() => popOverChangeFunction()}>
          <FormattedMessage id="new" />
        </Button>
      </Row>
      <ViaValidationAlert validationError={validationBox} />
      <Form.Item required label={<FormattedMessage id="question" />}>
        { form.getFieldDecorator('itemCode', {
          rules: [
            { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
          ],
          initialValue: undefined,
        })(
          <Row type="flex" align="middle">
            <Select
              autoFocus
              loading={isLoading}
              placeholder={intl.formatMessage({ id: 'selectOne' })}
              showSearch
              onChange={(value) => {
                form.setFieldsValue({ itemCode: value });
              }}
              onSearch={() => {
                form.setFieldsValue({ itemCode: undefined });
              }}
              value={form.getFieldValue('itemCode')}
              optionLabelProp="label"
              optionFilterProp="label"
              filterOption={
                (input, option) => (
                  option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
              notFoundContent={(
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<FormattedMessage id="noUnusedQuestions" />}
                />
              )}
            >
              {sortAlphabeticallyByAttribute(ddQuestions, 'itemDescription', false).map(item => (
                <Option key={item.itemCode} value={item.itemCode} label={item.itemDescription}>
                  <Row type="flex" justify="space-between">
                    <Text style={{ width: '60%', whiteSpace: 'pre-wrap' }}>{item.itemDescription}</Text>
                    <ViaChecklistTags dataType={item.itemDataType} digits={item.itemDigitCount} show active={item.active} />
                  </Row>
                </Option>
              ))}
            </Select>
          </Row>,
        )}
      </Form.Item>
      <Row type="flex" justify="end" style={{ marginTop: '-0.5em' }}>
        <Button loading={isLoading} size="small" style={{ marginRight: '0.5em' }} onClick={() => cancelFunction()}><FormattedMessage id="cancel" /></Button>
        <Button loading={isLoading} size="small" type="primary" htmlType="submit"><FormattedMessage id="save" /></Button>
      </Row>
    </Form>
  </Row>
))));
AddQuestionPopExisting.propTypes = {
  ddQuestions: PropTypes.arrayOf(PropTypes.object),
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  saveFunction: PropTypes.func,
  cancelFunction: PropTypes.func,
  popOverChangeFunction: PropTypes.func,
  isLoading: PropTypes.bool,
};
AddQuestionPopExisting.defaultProps = {
  ddQuestions: [],
  validationBox: null,
  saveFunction: () => {},
  cancelFunction: () => {},
  popOverChangeFunction: () => {},
  isLoading: false,
};

/**
 * Add Question (new)
 */
const AddQuestionPopNew = injectIntl((Form.create({ name: 'new-question' })(({
  form, intl, validationBox, saveFunction, cancelFunction, isLoading, duplicateCheck,
}) => {
  const [digitsDisabled, setDigitsDisabled] = useState(true);
  return (
    <Row>
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); saveFunction(form); }}>
        <ViaValidationAlert validationError={validationBox} />
        <Form.Item required label={<FormattedMessage id="question" />}>
          {form.getFieldDecorator('description', {
            rules: [
              {
                required: true,
                message: intl.formatMessage({ id: 'validationEmpty' }),
              },
              {
                validator(rule, value, callback) {
                  if (duplicateCheck(value)) {
                    callback(intl.formatMessage({ id: 'Duplicate' }));
                  }
                  callback();
                },
              },
            ],
            initialValue: '',
          })(
            <Row type="flex" align="middle">
              <Input
                placeholder={intl.formatMessage({ id: 'enter' })}
                maxLength={250}
              />
            </Row>,
          )}
        </Form.Item>
        <Form.Item required label={<FormattedMessage id="answerChoice" />}>
          {form.getFieldDecorator('answerType', {
            rules: [
              {
                required: true,
                message: intl.formatMessage({ id: 'validationEmpty' }),
              },
            ],
            initialValue: 'BOOLEAN',
          })(
            <Radio.Group
              buttonStyle="solid"
              onChange={(e) => {
                setDigitsDisabled(!(e.target.value === 'INTEGER' || e.target.value === 'INTEGER_NA'));
              }}
            >
              <Radio.Button value="BOOLEAN">
                <FormattedMessage id="fail" />
              &nbsp;|&nbsp;
                <FormattedMessage id="pass" />
              </Radio.Button>
              <Radio.Button value="BOOLEAN_NA">
                <FormattedMessage id="NA" />
              &nbsp;|&nbsp;
                <FormattedMessage id="fail" />
              &nbsp;|&nbsp;
                <FormattedMessage id="pass" />
              </Radio.Button>
              <Radio.Button value="INTEGER">
                <FormattedMessage id="number" />
              </Radio.Button>
              <Radio.Button value="INTEGER_NA">
                <FormattedMessage id="NA" />
              &nbsp;|&nbsp;
                <FormattedMessage id="number" />
              </Radio.Button>
            </Radio.Group>,
          )}
        </Form.Item>
        <Form.Item required label={<FormattedMessage id="numberRange" />}>
          {form.getFieldDecorator('digits', {
            rules: [
              {
                required: true,
                message: intl.formatMessage({ id: 'validationEmpty' }),
              },
            ],
            initialValue: 1,
          })(
            <Radio.Group
              buttonStyle="solid"
              disabled={digitsDisabled}
            >
              <Radio.Button value={1}>
                <FormattedMessage id="1digit" />
              </Radio.Button>
              <Radio.Button value={2}>
                <FormattedMessage id="2digits" />
              </Radio.Button>
              <Radio.Button value={3}>
                <FormattedMessage id="3digits" />
              </Radio.Button>
            </Radio.Group>,
          )}
        </Form.Item>
        <Form.Item label={<FormattedMessage id="preview" />}>
          <Row type="flex" justify="start">
            { form.getFieldValue('answerType').toString().endsWith('_NA')
              ? <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#3399ff"><FormattedMessage id="NA" /></Tag>
              : '' }
            { form.getFieldValue('answerType').toString().startsWith('BOOLEAN')
              ? (
                <div>
                  <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#e68a00"><FormattedMessage id="fail" /></Tag>
                  <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#00b33c"><FormattedMessage id="pass" /></Tag>
                </div>
              )
              : '' }
            { form.getFieldValue('answerType').toString().startsWith('INTEGER') && form.getFieldValue('digits') === 1
              ? <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#5c5c8a">0-9</Tag>
              : '' }
            { form.getFieldValue('answerType').toString().startsWith('INTEGER') && form.getFieldValue('digits') === 2
              ? <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#5c5c8a">0-99</Tag>
              : '' }
            { form.getFieldValue('answerType').toString().startsWith('INTEGER') && form.getFieldValue('digits') === 3
              ? <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#5c5c8a">0-999</Tag>
              : '' }
          </Row>
        </Form.Item>
        <Row type="flex" justify="end" style={{ marginTop: '-0.5em' }}>
          <Button loading={isLoading} size="small" style={{ marginRight: '0.5em' }} onClick={() => cancelFunction()}><FormattedMessage id="cancel" /></Button>
          <Button loading={isLoading} size="small" type="primary" htmlType="submit"><FormattedMessage id="save" /></Button>
        </Row>
      </Form>
    </Row>
  );
})));
AddQuestionPopNew.propTypes = {
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  saveFunction: PropTypes.func,
  cancelFunction: PropTypes.func,
  isLoading: PropTypes.bool,
  duplicateCheck: PropTypes.func,
};
AddQuestionPopNew.defaultProps = {
  validationBox: null,
  saveFunction: () => {},
  cancelFunction: () => {},
  isLoading: false,
  duplicateCheck: () => {},
};

/**
 * Checklist sortable (draggable) item.
 */
const ViaSortableTreeItem = ({
  dataId, active, title, icon, bold, dataType, digits, showTags, type, children,
  questions, onRemoveFunction, checklistConfig, reorderChecklist,
  questionIsLoading, questionValBox, questionCreateFunction, questionCancelFunction,
}) => {
  // Category
  const categoryId = dataId.toString().split('-')[1];

  // Take out questions already on any category
  const takenQuestions = [];
  const availQuestions = [];
  // useEffect(() => {
  checklistConfig.checkListCategories.forEach((cat) => {
    cat.checkListItems.forEach(itm => takenQuestions.push(itm));
  });
  const questionsCopy = JSON.parse(JSON.stringify(questions));
  questionsCopy.filter(item => takenQuestions.findIndex(taken => taken.itemCode === item.itemCode) === -1).map(itm => availQuestions.push(itm));

  // Extra validation helpers
  const doesQuestionDescriptionExist = (code, value) => {
    const matchedQuestion = questions.find(row => row.itemDescription.toString().toLowerCase() === (value || '').toString().toLowerCase());
    return matchedQuestion ? matchedQuestion.key !== code : false;
  };

  // Question popup
  const [addQuestionPopVisible, setAddQuestionPopVisible] = useState(false);
  const [addQuestionPopLoaded, setAddQuestionPopLoaded] = useState('');
  const [addQuestionPopTitle, setAddQuestionPopTitle] = useState('');
  const addPopOverStart = () => {
    setAddQuestionPopLoaded('ADD');
    setAddQuestionPopTitle('addQuestion');
  };
  const addPopOverChange = () => {
    // eslint-disable-next-line no-param-reassign
    setAddQuestionPopLoaded('NEW');
    setAddQuestionPopTitle('addNewQuestion');
  };
  const closePopOver = () => {
    setAddQuestionPopLoaded('');
    setAddQuestionPopVisible(false);
  };
  const cancelPopOver = () => {
    questionCancelFunction();
    closePopOver();
  };

  // Save New Question - existing
  const onSaveQuestionAdd = (form) => {
    form.validateFields((err, values) => {
      if (!err) {
        const amendChecklist = { ...JSON.parse(JSON.stringify(checklistConfig)) };
        const questionNew = questions.find(quest => quest.itemCode === values.itemCode);
        questionCreateFunction(amendChecklist, categoryId, questionNew, closePopOver);
      }
    });
  };

  // Save New Question - new
  const onSaveQuestionNew = (form) => {
    form.validateFields((err, values) => {
      if (!err) {
        const amendChecklist = { ...JSON.parse(JSON.stringify(checklistConfig)) };
        const questionNew = {
          active: true,
          itemDescription: values.description,
          itemDataType: values.answerType,
          itemDigitCount: values.digits,
        };
        questionCreateFunction(amendChecklist, categoryId, questionNew, closePopOver);
      }
    });
  };

  return (
    <div data-id={dataId}>
      <Row type="flex" align="middle" justify="space-between" className={type === 'cat' ? 'via-checklist-edit-category' : 'via-checklist-edit-item'}>
        <Col span={16}>
          <Row type="flex" align="middle">
            <Col style={{ width: icon ? '35px' : '10px' }}>
              <Row type="flex" align="middle">
                <Icon type="more" style={{ marginLeft: '-0.2em' }} />
                <Icon style={{ display: icon ? '' : 'none' }} className="via-checklist-icon" type={icon || 'right'} />
              </Row>
            </Col>
            <Col style={{ width: icon ? 'calc(100% - 35px)' : 'calc(100% - 10px)' }}>
              <Text style={{ fontWeight: bold ? 'bold' : '' }}>{title}</Text>
            </Col>
          </Row>
        </Col>
        <Col span={8}>
          <Row type="flex" align="middle" justify="end">
            <ViaChecklistTags dataType={dataType} digits={digits} show={showTags} active={active} />
            <Popover
              overlayStyle={{ width: '70vw', maxWidth: '750px' }}
              trigger="click"
              placement="leftTop"
              visible={addQuestionPopVisible}
              onVisibleChange={(vis) => {
                if (!vis) {
                  setAddQuestionPopVisible(false);
                }
              }}
              title={(
                <Row type="flex" justify="space-between" align="middle">
                  <FormattedMessage id={addQuestionPopTitle} />
                  <Icon type="close" onClick={() => cancelPopOver()} />
                </Row>
              )}
              content={(
                <div>
                  {addQuestionPopLoaded === 'ADD'
                    ? (
                      <AddQuestionPopExisting
                        ddQuestions={availQuestions}
                        cancelFunction={cancelPopOver}
                        saveFunction={onSaveQuestionAdd}
                        popOverChangeFunction={addPopOverChange}
                        isLoading={questionIsLoading}
                        validationBox={questionValBox}
                      />
                    )
                    : ''}
                  {addQuestionPopLoaded === 'NEW'
                    ? (
                      <AddQuestionPopNew
                        cancelFunction={cancelPopOver}
                        saveFunction={onSaveQuestionNew}
                        duplicateCheck={doesQuestionDescriptionExist}
                        isLoading={questionIsLoading}
                        validationBox={questionValBox}
                      />
                    )
                    : ''}
                </div>
              )}
            >
              <Button
                style={{
                  display: type === 'cat' ? '' : 'none', marginTop: '0.1em', marginBottom: '0.1em', marginRight: '0.5em', marginLeft: '0.5em',
                }}
                type="primary"
                size="small"
                icon="plus"
                onClick={() => { addPopOverStart(); setAddQuestionPopVisible(true); }}
              >
                <FormattedMessage id="addQuestion" />
              </Button>
            </Popover>
            <ConfirmButton style={{ display: type === 'cat' ? '' : 'none', marginTop: '0.1em', marginBottom: '0.1em' }} type="primary" size="small" onClick={() => { onRemoveFunction(dataId, checklistConfig); }} icon="delete"><FormattedMessage id="remove" /></ConfirmButton>
            <ConfirmButton
              style={{
                display: type === 'itm' ? '' : 'none', marginTop: '0.1em', marginBottom: '0.1em', marginLeft: '0.5em',
              }}
              className="via-checklist-button"
              type="primary"
              size="small"
              onClick={() => { onRemoveFunction(dataId, checklistConfig); }}
              icon="delete"
            >
              <FormattedMessage id="remove" />
            </ConfirmButton>
          </Row>
        </Col>
      </Row>
      <Row>
        <Sortable
          options={{
            group: 'items',
            animation: 50,
            easing: 'cubic-bezier(0.895, 0.03, 0.685, 0.22)',
            ghostClass: 'via-sortable-drag',
            emptyInsertThreshold: 50,
          }}
          onChange={(order) => {
            reorderChecklist(order, checklistConfig, dataId.split('-')[1]);
          }}
        >
          { children }
        </Sortable>
      </Row>
    </div>
  );
};
ViaSortableTreeItem.propTypes = {
  dataId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  active: PropTypes.bool.isRequired,
  bold: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
  dataType: PropTypes.string,
  digits: PropTypes.number,
  icon: PropTypes.string,
  showTags: PropTypes.bool,
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  questions: PropTypes.arrayOf(PropTypes.shape({
    itemCode: PropTypes.string.isRequired,
    itemDescription: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    itemDataType: PropTypes.string.isRequired,
    itemDigitCount: PropTypes.number,
  })),
  onRemoveFunction: PropTypes.func.isRequired,
  checklistConfig: PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    checkListCategories: PropTypes.arrayOf(PropTypes.object),
  }),
  reorderChecklist: PropTypes.func.isRequired,
  questionCreateFunction: PropTypes.func,
  questionCancelFunction: PropTypes.func,
  questionIsLoading: PropTypes.bool,
  questionValBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
};
ViaSortableTreeItem.defaultProps = {
  bold: false,
  children: null,
  dataType: '',
  digits: 0,
  icon: '',
  showTags: false,
  questions: [],
  checklistConfig: null,
  questionCreateFunction: () => {},
  questionCancelFunction: () => {},
  questionIsLoading: false,
  questionValBox: null,
};

/**
 * Checklist Edit Page
 * @param match
 * @param intl
 * @returns {*}
 * @constructor
 */
function EditChecklist({
  match, history, loadPageData, isLoading, checklists, categories, questions, checklistConfig,
  updateChecklistTitle, cancelChecklistTitle, isLoadingTitle, popoverValBoxTitle, checklistId,
  removeChecklistCategory, removeChecklistQuestion, reorderChecklist, isLoadingReorder, sessionUser,
  addCategoryToChecklist, addQuestionToChecklistCategory, isLoadingAddCategory, isLoadingAddQuestion,
  popoverValBoxAddCategory, popoverValBoxAddQuestion, cancelChecklistAddCategory, cancelChecklistAddQuestion,
}) {
  const [isMessageLoading, setIsMessageLoading] = useState(true);

  // Load checklist data on first entry - these only need to be loaded once
  // (checklist code coming from path)
  useEffect(() => {
    loadPageData(match.params.id);
  }, [loadPageData, match.params.id]);

  // Extra validation helpers
  const doesDescriptionExist = (value) => {
    const matchedChecklist = checklists.find(row => row.description.toString().toLowerCase() === (value || '').toString().toLowerCase());
    return matchedChecklist ? matchedChecklist.checkListCode !== checklistId : false;
  };

  const doesCategoryDescriptionExist = value => categories.some(row => row.categoryDescription.toString().toLowerCase() === (value || '').toString().toLowerCase());

  // Save description popover
  const [popoverVisible, setPopoverVisible] = useState(false);
  const onSaveDescEdit = (form) => {
    form.validateFields((err, values) => {
      if (!err) {
        const amendChecklist = { ...JSON.parse(JSON.stringify(checklistConfig)), description: values.description };
        updateChecklistTitle(amendChecklist, setPopoverVisible);
      }
    });
  };

  // Cancel description popover
  const onCancelDescEdit = (form) => {
    setPopoverVisible(false);
    form.setFieldsValue({ description: checklistConfig.description });
    cancelChecklistTitle(checklistConfig.description);
  };

  // Category popup
  const [addCategoryPopVisible, setAddCategoryPopVisible] = useState(false);
  const [addCategoryPopLoaded, setAddCategoryPopLoaded] = useState('');
  const [addCategoryPopTitle, setAddCategoryPopTitle] = useState('');
  const addPopOverStart = () => {
    setAddCategoryPopLoaded('ADD');
    setAddCategoryPopTitle('addCategory');
  };
  const addPopOverChange = () => {
    // eslint-disable-next-line no-param-reassign
    setAddCategoryPopLoaded('NEW');
    setAddCategoryPopTitle('addNewCategory');
  };
  const closePopOver = () => {
    setAddCategoryPopLoaded('');
    setAddCategoryPopVisible(false);
  };
  const cancelPopOver = () => {
    cancelChecklistAddCategory();
    closePopOver();
  };

  // Save New Category - existing
  const onSaveCategoryAdd = (form) => {
    form.validateFields((err, values) => {
      if (!err) {
        const amendChecklist = { ...JSON.parse(JSON.stringify(checklistConfig)) };
        const categoryNew = categories.find(cat => cat.categoryCode === values.categoryCode);
        addCategoryToChecklist(amendChecklist, categoryNew, closePopOver);
      }
    });
  };

  // Save New Category - new
  const onSaveCategoryNew = (form) => {
    form.validateFields((err, values) => {
      if (!err) {
        const amendChecklist = { ...JSON.parse(JSON.stringify(checklistConfig)) };
        const categoryNew = {
          active: true,
          categoryDescription: values.description,
        };
        addCategoryToChecklist(amendChecklist, categoryNew, closePopOver);
      }
    });
  };

  // Take out categories already on category
  const takenCategories = checklistConfig ? checklistConfig.checkListCategories : [];
  const availCategories = categories.filter(cat => takenCategories.findIndex(taken => taken.categoryCode === cat.categoryCode) === -1);

  // If come straight to page with invalid checklist ID or do not have authority to view page
  if (checklistConfig == null || !userIsAdmin(sessionUser)) {
    // eslint-disable-next-line no-implied-eval
    setTimeout(() => setIsMessageLoading(false), 4000);
    return (
      isLoading || isMessageLoading
        ? (
          <Row type="flex" justify="center" style={{ marginTop: '3em' }}>
            <Spin />
          </Row>
        )
        : (
          <Result
            status={!userIsAdmin(sessionUser) ? '403' : '404'}
            title={<FormattedMessage id={!userIsAdmin(sessionUser) ? 'accessRestricted' : 'invalidChecklist'} />}
            subTitle={<FormattedMessage id={!userIsAdmin(sessionUser) ? 'insufficientPermissions' : 'checklistMissing'} />}
          />
        )
    );
  }

  return (
    <div>
      <ConfigBackHeading
        titleValue={checklistConfig.description}
        active={checklistConfig.active}
        onBack={() => history.push(routeLocations.configure_checklists)}
        popoverActive
        popoverTitleId="editChecklistTitle"
        popoverCancelIcon
        popoverDescData={checklistConfig.description.toString()}
        popoverDescValidator={doesDescriptionExist}
        popoverCancelTextId="cancel"
        popoverCancelFunction={onCancelDescEdit}
        popoverSaveTextId="save"
        popoverSaveFunction={onSaveDescEdit}
        popoverLoading={isLoadingTitle}
        popoverValidationBox={popoverValBoxTitle}
        popoverVisible={popoverVisible}
        popoverSetVisibleFunction={setPopoverVisible}
        showEdit
        isLoading={isLoading || isLoadingReorder}
      >
        <Row style={{ marginLeft: '0.8em' }}>
          <Popover
            overlayStyle={{ width: '70vw', maxWidth: '750px' }}
            trigger="click"
            placement="rightTop"
            visible={addCategoryPopVisible}
            onVisibleChange={(vis) => {
              if (!vis) {
                setAddCategoryPopVisible(false);
              }
            }}
            title={(
              <Row type="flex" justify="space-between" align="middle">
                <FormattedMessage id={addCategoryPopTitle} />
                <Icon type="close" onClick={() => cancelPopOver()} />
              </Row>
            )}
            content={(
              <div>
                {addCategoryPopLoaded === 'ADD'
                  ? (
                    <AddCategoryPopExisting
                      ddCategories={availCategories}
                      cancelFunction={cancelPopOver}
                      saveFunction={onSaveCategoryAdd}
                      popOverChangeFunction={addPopOverChange}
                      isLoading={isLoadingAddCategory}
                      validationBox={popoverValBoxAddCategory}
                    />
                  )
                  : ''}
                {addCategoryPopLoaded === 'NEW'
                  ? (
                    <AddCategoryPopNew
                      cancelFunction={cancelPopOver}
                      saveFunction={onSaveCategoryNew}
                      duplicateCheck={doesCategoryDescriptionExist}
                      isLoading={isLoadingAddCategory}
                      validationBox={popoverValBoxAddCategory}
                    />
                  )
                  : ''}
              </div>
            )}
          >
            <Button
              style={{ marginBottom: '0.5em' }}
              type="primary"
              size="small"
              onClick={() => { addPopOverStart(); setAddCategoryPopVisible(true); }}
              icon="plus"
            >
              <FormattedMessage id="addCategory" />
            </Button>
          </Popover>
        </Row>
        {isLoading
          ? (
            <Row>
              <Empty image={null} description=" ">
                <Spin />
              </Empty>
            </Row>
          )
          : (
            <Row style={{
              marginLeft: '0.5em',
              marginRight: '0.5em',
            }}
            >
              <Empty
                style={{ display: checklistConfig.checkListCategories.length > 0 ? 'none' : '' }}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<FormattedMessage id="incompleteChecklist" />}
              />
              <Sortable
                options={{
                  group: 'categories',
                  animation: 50,
                  easing: 'cubic-bezier(0.77, 0, 0.175, 1)',
                  ghostClass: 'via-sortable-drag',
                }}
                onChange={(order) => {
                  reorderChecklist(order, checklistConfig, -1);
                }}
              >
                {
                checklistConfig.checkListCategories.map((category) => {
                  const catKey = `${checklistConfig.checkListCode}-${category.categoryCode}`;
                  return (
                    <ViaSortableTreeItem
                      key={catKey}
                      dataId={catKey}
                      active={category.active}
                      title={category.categoryDescription}
                      type="cat"
                      questions={questions}
                      onRemoveFunction={removeChecklistCategory}
                      checklistConfig={checklistConfig}
                      reorderChecklist={reorderChecklist}
                      questionCreateFunction={addQuestionToChecklistCategory}
                      questionCancelFunction={cancelChecklistAddQuestion}
                      questionIsLoading={isLoadingAddQuestion}
                      questionValBox={popoverValBoxAddQuestion}
                    >
                      {
                        category.checkListItems.length === 0
                          ? (
                            <div data-id="ignore">
                              <Row type="flex" align="middle" justify="start" className="via-checklist-no-questions">
                                <Text>
                                  <FormattedMessage id="noQuestions" />
                                </Text>
                              </Row>
                            </div>
                          )
                          : category.checkListItems.map((item) => {
                            const itemKey = `${checklistConfig.checkListCode}-${category.categoryCode}-${item.itemCode}`;
                            return (
                              <ViaSortableTreeItem
                                key={itemKey}
                                dataId={itemKey}
                                active={item.active}
                                title={item.itemDescription}
                                icon="question-circle"
                                showTags
                                dataType={item.itemDataType}
                                digits={item.itemDigitCount}
                                type="itm"
                                onRemoveFunction={removeChecklistQuestion}
                                checklistConfig={checklistConfig}
                                reorderChecklist={reorderChecklist}
                              />
                            );
                          })
                      }
                    </ViaSortableTreeItem>
                  );
                })
              }
              </Sortable>
            </Row>
          )
        }
      </ConfigBackHeading>
    </div>
  );
}

// Validate the prop type parameters.
EditChecklist.propTypes = {
  match: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  isLoading: PropTypes.bool,
  checklistId: PropTypes.string,
  loadPageData: PropTypes.func.isRequired,
  checklists: PropTypes.arrayOf(PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
  })),
  categories: PropTypes.arrayOf(PropTypes.shape({
    categoryCode: PropTypes.string.isRequired,
    categoryDescription: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
  })),
  questions: PropTypes.arrayOf(PropTypes.shape({
    itemCode: PropTypes.string.isRequired,
    itemDescription: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    itemDataType: PropTypes.string.isRequired,
    itemDigitCount: PropTypes.number,
  })),
  checklistConfig: PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    checkListCategories: PropTypes.arrayOf(PropTypes.object),
  }),
  updateChecklistTitle: PropTypes.func.isRequired,
  cancelChecklistTitle: PropTypes.func.isRequired,
  isLoadingTitle: PropTypes.bool.isRequired,
  isLoadingReorder: PropTypes.bool.isRequired,
  isLoadingAddCategory: PropTypes.bool.isRequired,
  isLoadingAddQuestion: PropTypes.bool.isRequired,
  popoverValBoxTitle: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  popoverValBoxAddCategory: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  popoverValBoxAddQuestion: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  removeChecklistCategory: PropTypes.func,
  removeChecklistQuestion: PropTypes.func,
  reorderChecklist: PropTypes.func,
  addCategoryToChecklist: PropTypes.func,
  addQuestionToChecklistCategory: PropTypes.func,
  cancelChecklistAddCategory: PropTypes.func,
  cancelChecklistAddQuestion: PropTypes.func,
  sessionUser: PropTypes.shape({ displayName: PropTypes.string.isRequired }).isRequired,
};

EditChecklist.defaultProps = {
  match: '',
  isLoading: false,
  checklists: [],
  categories: [],
  questions: [],
  checklistConfig: null,
  popoverValBoxTitle: null,
  popoverValBoxAddCategory: null,
  popoverValBoxAddQuestion: null,
  checklistId: null,
  removeChecklistCategory: () => {},
  removeChecklistQuestion: () => {},
  reorderChecklist: () => {},
  addCategoryToChecklist: () => {},
  addQuestionToChecklistCategory: () => {},
  cancelChecklistAddCategory: () => {},
  cancelChecklistAddQuestion: () => {},
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectChecklistPageDataRequest(state);
  const updateChecklistTitleRequest = selectUpdateChecklistTitleRequest(state);
  const reorderChecklistRequest = selectReorderChecklistRequest(state);
  const addCatChecklistRequest = selectAddCategoryChecklistRequest(state);
  const addItmChecklistRequest = selectAddItemChecklistRequest(state);
  const checklists = selectAllChecklists(state);
  const categories = selectAllChecklistCategories(state);
  const questions = selectAllChecklistItems(state);
  const editState = selectEditState(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    isLoadingTitle: updateChecklistTitleRequest ? updateChecklistTitleRequest.isLoading : false,
    isLoadingReorder: reorderChecklistRequest ? reorderChecklistRequest.isLoading : false,
    isLoadingAddCategory: addCatChecklistRequest ? addCatChecklistRequest.isLoading : false,
    isLoadingAddQuestion: addItmChecklistRequest ? addItmChecklistRequest.isLoading : false,
    popoverValBoxTitle: updateChecklistTitleRequest ? updateChecklistTitleRequest.error : undefined,
    popoverValBoxAddCategory: addCatChecklistRequest ? addCatChecklistRequest.error : undefined,
    popoverValBoxAddQuestion: addItmChecklistRequest ? addItmChecklistRequest.error : undefined,
    checklistConfig: editState.checklistData || null,
    checklistId: editState.checklistId || null,
    checklists: checklists || [],
    categories: categories || [],
    questions: questions || [],
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: checklistId => dispatch(requestChecklistPageData(checklistId)),
  updateChecklistTitle: (checklist, popOverVisibleFunc) => dispatch(requestUpdateChecklistTitle(checklist, popOverVisibleFunc)),
  cancelChecklistTitle: prevDesc => dispatch(cancelUpdateChecklistTitleRequest(prevDesc)),
  removeChecklistCategory: (catKey, checklist) => dispatch(requestChecklistCategoryRemoval(catKey, checklist)),
  removeChecklistQuestion: (itmKey, checklist) => dispatch(requestChecklistQuestionRemoval(itmKey, checklist)),
  reorderChecklist: (order, checklist, categoryKey) => dispatch(requestChecklistReorder(order, checklist, categoryKey)),
  addCategoryToChecklist: (checklist, category, popOverCloseFunc) => dispatch(requestAddChecklistCategory(checklist, category, popOverCloseFunc)),
  cancelChecklistAddCategory: () => dispatch(cancelChecklistAddCategoryRequest()),
  addQuestionToChecklistCategory: (checklist, categoryId, question, popOverCloseFunc) => (
    dispatch(requestAddChecklistQuestion(checklist, categoryId, question, popOverCloseFunc))),
  cancelChecklistAddQuestion: () => dispatch(cancelChecklistAddQuestionRequest()),
});

/**
 * Connect the redux state to our component.
 */
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditChecklist))
